import { outcomeOf, type LatLon, type RunPlan, type RunStatus } from "@wayfind/core";

/**
 * A map that can only draw what is true.
 *
 * It is not a tiled basemap, and that is a decision rather than a gap.
 * There is no tile provider this product can ship without an account, and
 * an account is the one thing it does not have. What it draws instead is
 * the road the run will actually drive, unpacked from **this device's own
 * route graph** — the same geometry the arrival time was computed over, so
 * the picture and the number cannot disagree.
 *
 * The vehicle is drawn if and only if the caller passes a position, and
 * the caller only has one when the position claim is `live`. A coarse run
 * and a stale fix therefore cannot produce a marker here by any route
 * through the code — there is no prop to pass.
 */
export function Trace({
  plan,
  geometry,
  status,
  position,
  myStopIndex
}: {
  plan: RunPlan;
  geometry: [number, number][];
  status: RunStatus;
  position: LatLon | null;
  myStopIndex: number | null;
}): JSX.Element | null {
  const points = geometry.length ? geometry : plan.stops.map(stop => [stop.lat, stop.lon] as [number, number]);
  if (points.length < 2) return null;

  const lats = points.map(point => point[0]);
  const lons = points.map(point => point[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);

  // Equirectangular, corrected for latitude so the shape of a city is not
  // stretched. Fine at this scale and honest about being a sketch.
  const midLat = (minLat + maxLat) / 2;
  const kx = Math.cos((midLat * Math.PI) / 180);
  const spanX = Math.max((maxLon - minLon) * kx, 1e-6);
  const spanY = Math.max(maxLat - minLat, 1e-6);
  const width = 640;
  const pad = 34;
  const height = Math.round(
    Math.min(430, Math.max(230, ((width - pad * 2) * spanY) / spanX + pad * 2))
  );

  const scale = Math.min((width - pad * 2) / spanX, (height - pad * 2) / spanY);
  const offsetX = (width - spanX * scale) / 2;
  const offsetY = (height - spanY * scale) / 2;

  function project(lat: number, lon: number): [number, number] {
    return [
      offsetX + (lon - minLon) * kx * scale,
      offsetY + (maxLat - lat) * scale
    ];
  }

  // The part of the road already behind the run is drawn settled, so the
  // picture advances with the work rather than sitting still.
  const drivenIndex = splitIndexFor(points, plan, status.stopIndex, project);
  const driven = points.slice(0, Math.max(2, drivenIndex + 1));
  const ahead = points.slice(Math.max(0, drivenIndex));

  const vehicle = position ? project(position.lat, position.lon) : null;

  // Amber is deliberately *not* painted along the road from the van to your
  // door, tempting as that is: on a five-drop round where yours is the
  // fourth, that range is most of the route, and a mark where amber fills
  // the path says the opposite of what this product sells. So the road
  // stays track, and amber is spent on two small things — the van, which
  // is live, and your own stop, which is yours.

  return (
    <svg
      className="trace"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={
        vehicle
          ? "The route, with the driver's current position marked."
          : "The route and its stops. No position is drawn."
      }
    >
      <path className="trace__road" d={pathOf(ahead, project)} />
      {driven.length > 1 ? <path className="trace__road trace__road--done" d={pathOf(driven, project)} /> : null}

      {plan.stops.map(stop => {
        const [x, y] = project(stop.lat, stop.lon);
        const outcome = outcomeOf(status.outcomes, stop.index);
        const classes = [
          "trace__stop",
          outcome === "delivered" ? "trace__stop--done" : "",
          outcome === "skipped" || outcome === "failed" ? "trace__stop--halt" : "",
          stop.index === myStopIndex ? "trace__stop--mine" : ""
        ].filter(Boolean).join(" ");
        return (
          <circle
            key={stop.index}
            className={classes}
            cx={x}
            cy={y}
            r={stop.index === myStopIndex ? 8 : 5.5}
          />
        );
      })}

      {vehicle ? (
        <g>
          <circle className="trace__van-halo" cx={vehicle[0]} cy={vehicle[1]} r={17} />
          <circle className="trace__van" cx={vehicle[0]} cy={vehicle[1]} r={7} />
        </g>
      ) : null}
    </svg>
  );
}

function pathOf(
  points: [number, number][],
  project: (lat: number, lon: number) => [number, number]
): string {
  return points
    .map((point, index) => {
      const [x, y] = project(point[0], point[1]);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
}

/** The polyline vertex nearest the last stop the run has dealt with. */
function splitIndexFor(
  points: [number, number][],
  plan: RunPlan,
  stopIndex: number,
  project: (lat: number, lon: number) => [number, number]
): number {
  const stop = plan.stops.find(candidate => candidate.index === stopIndex);
  if (!stop) return 0;
  const target = project(stop.lat, stop.lon);
  let best = 0;
  let bestDistance = Infinity;
  for (let i = 0; i < points.length; i++) {
    const point = points[i]!;
    const [x, y] = project(point[0], point[1]);
    const distance = Math.hypot(x - target[0], y - target[1]);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  }
  return best;
}

/**
 * What a stops-only run gets instead of a map.
 *
 * Visually a different object, not the live view with the dot removed: no
 * frame, no empty canvas, no "waiting for position". §11 coarse is a
 * decision about what a leaked link is worth, and a screen that looks like
 * it is loading a position implies one is coming.
 */
export function NoPosition({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <section className="withheld">
      <h2 className="withheld__title">{title}</h2>
      <p className="withheld__body">{body}</p>
    </section>
  );
}

/**
 * The map a real recipient gets: the van, their own door, and the road
 * between the two.
 *
 * `Trace` above needs the whole plan, and a real dispatched customer will
 * never have one — §20.8 keeps every other doorstep off the wire, which
 * is the point. So for months the map simply did not render for anybody
 * outside the demo. What a recipient *does* have, out of their own copy
 * of the link, is one door; and what the page has already computed is the
 * road from the van to it.
 *
 * That road is drawn as **track, not amber**, and the distinction is the
 * honest one. It is not the route the van will take — the van has other
 * doors to call at first, which is exactly why the number above it says
 * "not before". Drawing it amber would make a floor look like a plan.
 * Amber is spent on the two things that are true without qualification:
 * the van, which is live, and this door, which is yours.
 */
export function MyLeg({
  geometry,
  position,
  door,
  settled
}: {
  geometry: [number, number][];
  position: LatLon | null;
  door: LatLon;
  /** Delivered, skipped or failed — the leg is history, not a journey. */
  settled: boolean;
}): JSX.Element | null {
  const points: [number, number][] = geometry.length >= 2
    ? geometry
    : position
      ? [[position.lat, position.lon], [door.lat, door.lon]]
      : [];
  if (points.length < 2) return null;

  const lats = points.map(point => point[0]);
  const lons = points.map(point => point[1]);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const width = 640;
  const height = 300;
  const pad = 34;
  const spanLat = Math.max(maxLat - minLat, 1e-5);
  const spanLon = Math.max(maxLon - minLon, 1e-5);
  const scale = Math.min((width - pad * 2) / spanLon, (height - pad * 2) / spanLat);
  const offsetX = (width - spanLon * scale) / 2;
  const offsetY = (height - spanLat * scale) / 2;

  function project(lat: number, lon: number): [number, number] {
    return [offsetX + (lon - minLon) * scale, offsetY + (maxLat - lat) * scale];
  }

  const road = points
    .map((point, index) => {
      const [x, y] = project(point[0], point[1]);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  const here = project(door.lat, door.lon);
  const van = position ? project(position.lat, position.lon) : null;

  return (
    <svg
      className="trace"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={
        van
          ? "Your address, the driver's current position, and the road between them."
          : "Your address. No position is being published."
      }
    >
      <path className={`trace__road${settled ? " trace__road--done" : ""}`} d={road} />
      {van ? (
        <g>
          <circle className="trace__van-halo" cx={van[0]} cy={van[1]} r={17} />
          <circle className="trace__van" cx={van[0]} cy={van[1]} r={7} />
        </g>
      ) : null}
      <circle className="trace__stop trace__stop--mine" cx={here[0]} cy={here[1]} r={8} />
    </svg>
  );
}
